/**
 * 自社入札フィルタリング (= market board タブ用)。
 *
 * 既存 ListBiddingIntradayOrders の結果から選択中コマに一致する行を抽出する。
 * read-only 表示用 (= Phase 3 まで編集・取消は実装しない)。
 */
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'

import type { IntradayOrderRow } from '~/hooks/bidding/useListBiddingIntradayOrders'
import { JEPX_AREA_CODES } from '~/models/area'
import { defineFilterCodec, listOf, oneOf } from '~/models/filterCodec'
import type { IntradaySideFilter } from '~/pages/IntradayBidding/models/globalFilters'

/**
 * 自社注文から選択中コマの行を抽出する。
 *
 * @param orders - 全注文一覧
 * @param timeCode - 選択中コマ番号 (1〜48)
 * @returns 選択コマに一致する注文行
 */
export function filterMyBidsByKoma(
  orders: IntradayOrderRow[],
  timeCode: number,
): IntradayOrderRow[] {
  return orders.filter((o) => o.timeCode === timeCode)
}

/**
 * 自社入札テーブルの絞り込み (AC-BIN-103)。 選択方式と既定値は全体フィルター (AC-BIN-102) の
 * エリア・売買区分と同じで、 板発注タブの中だけで持つ (全体フィルターには反映しない)。
 * エリアは空 = 「すべて」 (design `intraday-price.html` の `myBidsAreaSel` と同じ規約)。
 */
export type MyBidsFilters = {
  areas: AreaCode[]
  side: IntradaySideFilter
}

export const myBidsFiltersCodec = defineFilterCodec<MyBidsFilters>({
  defaults: () => ({ areas: [], side: 'all' }),
  fields: {
    areas: listOf(oneOf<AreaCode>(JEPX_AREA_CODES)),
    side: oneOf<IntradaySideFilter>(['all', 'buy', 'sell']),
  },
})

/** 自社入札テーブルに出す行。 エリアは空 = すべて、 売買区分は `all` = すべて。 */
export function filterMyBidsByAreaAndSide(
  orders: readonly IntradayOrderRow[],
  { areas, side }: MyBidsFilters,
): IntradayOrderRow[] {
  return orders.filter((o) => {
    if (areas.length > 0 && !areas.includes(o.areaCode)) return false
    if (side === 'buy' && o.direction !== Side.BUY) return false
    if (side === 'sell' && o.direction !== Side.SELL) return false
    return true
  })
}
